/**
 * The home of play.makeit.app (see site.ts): only playing. The landing's code
 * box and demos, the classroom student link and a way to the whole web
 * (makeit.app) to create projects. No sign-in, Home or editor here.
 */
import { useEffect, useState } from "react";
import type { ProjectSource } from "../data/loadProject";
import { libraryUrl } from "../data/media";
import { forgetSharedCode, loadSharedCodes } from "../data/sharedCodes";
import { currentLanguage, t } from "../player/i18n";
import { RTL_LANGUAGES } from "../player/rtl";
import { MAIN_ORIGIN } from "../site";
import { CodeCard, DemoCards, LanguagePicker } from "./Landing";
import { landingText } from "./landingText";
import { TypeBadge, templateArt } from "./widgets";
import "./landing.css";

/**
 * The projects played with a code in this browser (data/sharedCodes.ts), as the
 * Unity app's student mode kept them: tap one to play it again, × takes it off
 * the list. Nothing shows until a code has been played.
 */
function SharedWithMe({ onPlay }: { onPlay: (source: ProjectSource) => void }) {
  const [list, setList] = useState(loadSharedCodes);
  if (!list.length) return null;
  return (
    <section className="lp-section lp-shared">
      <div className="lp-wrap">
        <header className="lp-heading">
          <h2>{t("sharedWithMe")}</h2>
          <p>{t("redeemPlayHint")}</p>
        </header>
        <div className="pgrid">
          {list.map((s) => (
            <div key={s.code} className="lp-shared-card">
              <button className="pcard" onClick={() => onPlay({ kind: "code", code: s.code })}>
                <span className="pcard-art" style={templateArt(s.template)}>
                  <TypeBadge type={s.type} />
                </span>
                <span className="pcard-name" dir="auto">
                  {s.name}
                </span>
              </button>
              <button
                className="lp-shared-x"
                title={t("removeShared")}
                aria-label={`${t("removeShared")}: ${s.name}`}
                onClick={() => {
                  forgetSharedCode(s.code);
                  setList(loadSharedCodes());
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PlayHome({ onPlay, error }: { onPlay: (source: ProjectSource) => void; error?: string }) {
  const L = landingText();
  const lang = currentLanguage();
  useEffect(() => {
    document.title = "Make It";
  }, []);
  return (
    <div className="lp lp-play" dir={RTL_LANGUAGES.has(lang) ? "rtl" : "ltr"} lang={lang}>
      <header className="lp-nav">
        <div className="lp-wrap lp-nav-in">
          <a className="lp-logo" href={MAIN_ORIGIN} aria-label="Make It">
            <img src={libraryUrl("ui/logo.png")} alt="Make It" />
          </a>
          <div className="lp-actions">
            <LanguagePicker label={L.nav.language} />
            <a className="lp-link lp-play-student" href="#/student">
              {L.nav.student}
            </a>
            <a className="lp-btn lp-btn-primary lp-hide-s" href={MAIN_ORIGIN}>
              {L.hero.start}
            </a>
          </div>
        </div>
      </header>
      <main>
        <section className="lp-hero lp-play-hero">
          <div className="lp-wrap">
            <CodeCard L={L} onPlay={onPlay} error={error} />
          </div>
        </section>
        <SharedWithMe onPlay={onPlay} />
        <section className="lp-section lp-demos">
          <div className="lp-wrap">
            <header className="lp-heading">
              <h2>{L.demos.title}</h2>
              <p>{L.demos.sub}</p>
            </header>
            <DemoCards L={L} onPlay={onPlay} />
          </div>
        </section>
      </main>
    </div>
  );
}
